import {
  ClipboardCheck,
  Eye,
  Loader2,
  RefreshCw,
  Trash2,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  cancelProfessionalInvite,
  inviteProfessional,
  listPendingProfessionalInvites,
  listProfessionals,
  listProfessions,
  deleteProfessional,
  resendProfessionalInvite,
  type PendingProfessionalInvite,
  type ProfessionalUser,
} from "@/services/professionalsService";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { serverMessage } from "@/lib/apiError";
import { formatDisplayDate } from "@/lib/dateFormat";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";

const PROFESSIONALS_QUERY_KEY = ["platform", "professionals"] as const;
const PROFESSIONAL_INVITES_QUERY_KEY = ["platform", "professional-invites"] as const;
const PROFESSIONS_QUERY_KEY = ["platform", "professions"] as const;

function statusVariant(status: string): "success" | "warning" | "danger" | "neutral" {
  if (status === "active") return "success";
  if (status === "pending") return "warning";
  if (status === "disabled") return "danger";
  return "neutral";
}

function displayNameOf(u: ProfessionalUser): string {
  const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return full || u.displayName || u.email;
}

/**
 * ONE SECTION FOR EVERY PROFESSION (spec 2f0e5645).
 *
 * This replaces /notaries and /accountants, which were the same 700-line page
 * twice. The cost of that was never duplication — it was that adding a
 * profession meant writing a third copy, in the one place a human has to work
 * when a profession is added, which is exactly where "adding a profession is
 * registry data" stopped being true.
 *
 * NOTHING HERE NAMES A PROFESSION. The filter's options come from the
 * registry, the invite dialog's selector comes from the registry, and the
 * detail link carries an id rather than a discipline. A profession this file
 * has never heard of appears in all three by existing.
 */
export function ProfessionalsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();

  const [professionFilter, setProfessionFilter] = useState<string>("all");
  const [fiscalFilter, setFiscalFilter] = useState<
    "all" | "complete" | "cannot_issue_cfe" | "not_declared"
  >("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteProfession, setInviteProfession] = useState<string>("");
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancelDialogInvite, setCancelDialogInvite] =
    useState<PendingProfessionalInvite | null>(null);
  const [toDelete, setToDelete] = useState<ProfessionalUser | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const isRoot = user?.platformRole === "root";

  const professionsQuery = useQuery({
    queryKey: PROFESSIONS_QUERY_KEY,
    queryFn: listProfessions,
  });
  const professionalsQuery = useQuery({
    queryKey: PROFESSIONALS_QUERY_KEY,
    queryFn: () => listProfessionals(),
  });
  const invitesQuery = useQuery({
    queryKey: PROFESSIONAL_INVITES_QUERY_KEY,
    queryFn: () => listPendingProfessionalInvites(),
  });

  const professions = useMemo(() => professionsQuery.data ?? [], [professionsQuery.data]);
  const professionals = useMemo(
    () => professionalsQuery.data ?? [],
    [professionalsQuery.data],
  );
  const invites = useMemo(() => invitesQuery.data ?? [], [invitesQuery.data]);

  /** The registry's own word for a slug, for rows and for invites alike. */
  const professionLabel = useMemo(() => {
    const bySlug = new Map(professions.map((p) => [p.slug, p.displayName]));
    return (slug: string): string => bySlug.get(slug) ?? slug;
  }, [professions]);

  const combinedRows = useMemo(() => {
    const byProfession = <T extends { professionSlug: string }>(rows: T[]): T[] =>
      professionFilter === "all"
        ? rows
        : rows.filter((r) => r.professionSlug === professionFilter);

    const peopleRows = byProfession(professionals).map((u) => ({
      type: "professional" as const,
      data: u,
    }));
    const inviteRows = byProfession(invites).map((inv) => ({
      type: "invite" as const,
      data: inv,
    }));
    // An invite is not a professional yet and has no declaration, so any
    // fiscal filter necessarily excludes invites rather than guessing.
    const filtered =
      fiscalFilter === "all"
        ? peopleRows
        : peopleRows.filter((r) => r.data.fiscalStatus === fiscalFilter);
    return fiscalFilter === "all" ? [...filtered, ...inviteRows] : filtered;
  }, [professionals, invites, fiscalFilter, professionFilter]);

  useEffect(() => {
    pagination.setTotal(combinedRows.length);
  }, [combinedRows.length, pagination]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return combinedRows.slice(start, start + pagination.limit);
  }, [combinedRows, pagination.page, pagination.limit]);

  const inviteMutation = useMutation({
    mutationFn: (args: { professionSlug: string; email: string }) =>
      inviteProfessional(args.professionSlug, args.email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROFESSIONAL_INVITES_QUERY_KEY });
      setInviteOpen(false);
      setInviteEmail("");
      toast.success(t("professionalsSection.inviteSent"));
    },
    /*
      THE SERVER'S OWN REASON, because this endpoint now has real ones: an
      "Unknown profession" when the slug is not in the registry, and the usual
      refusals for a duplicate or malformed email. A generic toast throws away
      the only part of the response that says what to do next.
    */
    onError: (error) => {
      toast.error(serverMessage(error) ?? t("tenants.edit.errorUpdate"));
    },
  });

  const resendMutation = useMutation({
    mutationFn: (inv: PendingProfessionalInvite) =>
      resendProfessionalInvite(inv.id, inv.professionSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROFESSIONAL_INVITES_QUERY_KEY });
      setResendingId(null);
      toast.success(t("professionalsSection.resendSuccess"));
    },
    onError: (error) => {
      toast.error(serverMessage(error) ?? t("tenants.edit.errorUpdate"));
      setResendingId(null);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (inv: PendingProfessionalInvite) =>
      cancelProfessionalInvite(inv.id, inv.professionSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROFESSIONAL_INVITES_QUERY_KEY });
      setCancelDialogInvite(null);
      setCancellingId(null);
      toast.success(t("professionalsSection.inviteCancelled"));
    },
    onError: (error) => {
      toast.error(serverMessage(error) ?? t("tenants.edit.errorUpdate"));
      setCancellingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProfessional(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROFESSIONALS_QUERY_KEY });
      setToDelete(null);
      setDeletingId(null);
      toast.success(t("professionalsSection.deleteSuccess"));
    },
    onError: (error) => {
      toast.error(serverMessage(error) ?? t("tenants.edit.errorUpdate"));
      setDeletingId(null);
    },
  });

  const loading =
    professionalsQuery.isLoading || invitesQuery.isLoading || professionsQuery.isLoading;
  const isRefreshing = professionalsQuery.isFetching || invitesQuery.isFetching;
  const totalRows = combinedRows.length;
  const columnCount = 7;

  const handleRefresh = (): void => {
    void professionalsQuery.refetch();
    void invitesQuery.refetch();
  };

  const rowActions = (p: ProfessionalUser) => [
    ...(p.activationStatus === "pending"
      ? [
          {
            label: t("professionalsSection.reviewApplication"),
            icon: ClipboardCheck,
            onClick: () => navigate(`/profesionales/${p.id}`),
          },
        ]
      : []),
    {
      label: t("professionalsSection.viewDetails"),
      icon: Eye,
      onClick: () => navigate(`/profesionales/${p.id}`),
    },
    ...(isRoot
      ? [
          {
            label: t("professionalsSection.delete"),
            icon: Trash2,
            variant: "destructive" as const,
            onClick: () => setToDelete(p),
          },
        ]
      : []),
  ];

  const inviteActions = (inv: PendingProfessionalInvite) => [
    {
      label: t("professionalsSection.resend"),
      icon: RefreshCw,
      onClick: () => {
        setResendingId(inv.id);
        resendMutation.mutate(inv);
      },
    },
    {
      label: t("professionalsSection.cancelInvite"),
      icon: XCircle,
      variant: "destructive" as const,
      onClick: () => setCancelDialogInvite(inv),
    },
  ];

  const statusCell = (p: ProfessionalUser): JSX.Element => (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
      <StatusBadge variant={statusVariant(p.status)}>
        {t(`users.page.status.${p.status}`)}
      </StatusBadge>
      {p.activationStatus === "pending" ? (
        <StatusBadge variant="warning">{t("professionalsSection.activationPendingBadge")}</StatusBadge>
      ) : null}
      {/* Only shown when MISSING, as on the pages this replaces: a tick beside
          every compliant row would bury the few that need chasing (spec
          af19d370 C-7), and the two states are two different actions. */}
      {p.fiscalStatus !== "complete" ? (
        <StatusBadge
          variant={p.fiscalStatus === "cannot_issue_cfe" ? "softDanger" : "warning"}
        >
          {t(`professionals.fiscalStatus_${p.fiscalStatus}`)}
        </StatusBadge>
      ) : null}
    </div>
  );

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("professionalsSection.title")}
        description={t("professionalsSection.subtitle")}
        actions={
          <Button
            type="button"
            onClick={() => {
              // Default to the only profession when there is one; otherwise
              // make the admin choose, rather than guessing on their behalf.
              setInviteProfession(professions.length === 1 ? professions[0].slug : "");
              setInviteOpen(true);
            }}
            className="transition-colors duration-150"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t("professionalsSection.invite")}
          </Button>
        }
      />

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* THE REGISTRY IS THE OPTION LIST. A hardcoded pair here would be the
            closed set this section exists to remove, reintroduced in the one
            place a human looks when a profession is added. */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t("professionalsSection.professionFilterLabel")}
          </span>
          <Select value={professionFilter} onValueChange={setProfessionFilter}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("professionalsSection.professionFilterAll")}
              </SelectItem>
              {professions.map((p) => (
                <SelectItem key={p.slug} value={p.slug}>
                  {p.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t("professionals.fiscalFilterLabel")}
          </span>
          <Select
            value={fiscalFilter}
            onValueChange={(v) => setFiscalFilter(v as typeof fiscalFilter)}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("professionals.fiscalFilterAll")}</SelectItem>
              <SelectItem value="not_declared">
                {t("professionals.fiscalStatus_not_declared")}
              </SelectItem>
              <SelectItem value="cannot_issue_cfe">
                {t("professionals.fiscalStatus_cannot_issue_cfe")}
              </SelectItem>
              <SelectItem value="complete">
                {t("professionals.fiscalStatus_complete")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <TableCard className="mt-4">
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : (
          <>
            {totalRows === 0 ? (
              <div className="flex min-h-[40vh] items-center justify-center p-4">
                <EmptyState
                  title={t("professionalsSection.empty")}
                  message={t("professionalsSection.emptyHint")}
                />
              </div>
            ) : (
              <ResponsiveListSplit
                table={
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("professionalsSection.columnName")}</TableHead>
                        <TableHead>{t("professionalsSection.columnEmail")}</TableHead>
                        <TableHead>{t("professionalsSection.columnProfession")}</TableHead>
                        <TableHead>{t("professionalsSection.columnStatus")}</TableHead>
                        <TableHead>{t("professionalsSection.columnCreated")}</TableHead>
                        <TableHead>{t("professionalsSection.columnInvitedBy")}</TableHead>
                        <TableHead className="w-[80px]">{t("professionalsSection.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columnCount} className="h-32">
                            <EmptyState
                              title={t("professionalsSection.empty")}
                              message={t("professionalsSection.emptyHint")}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {paginatedRows.map((row) =>
                        row.type === "professional" ? (
                          <TableRow key={`pro-${row.data.id}`}>
                            <TableCell className="font-medium">
                              {displayNameOf(row.data)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.email}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.professionDisplayName ??
                                professionLabel(row.data.professionSlug)}
                            </TableCell>
                            <TableCell>{statusCell(row.data)}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDisplayDate(row.data.createdAt)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">—</TableCell>
                            <TableCell>
                              <TableActionsMenu actions={rowActions(row.data)} />
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow key={`invite-${row.data.id}`}>
                            <TableCell className="font-medium">—</TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.email}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {professionLabel(row.data.professionSlug)}
                            </TableCell>
                            <TableCell>
                              <StatusBadge variant={row.data.expired ? "danger" : "warning"}>
                                {row.data.expired
                                  ? t("professionalsSection.statusExpired")
                                  : t("professionalsSection.statusPendingInvite")}
                              </StatusBadge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDisplayDate(row.data.expiresAt)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.invitedByEmail ?? "—"}
                            </TableCell>
                            <TableCell>
                              {isRoot ? (
                                resendingId === row.data.id ? (
                                  <div className="flex h-9 w-9 items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                  </div>
                                ) : (
                                  <TableActionsMenu
                                    disabled={resendingId !== null || cancellingId !== null}
                                    actions={inviteActions(row.data)}
                                  />
                                )
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="divide-y divide-border">
                    {paginatedRows.map((row) =>
                      row.type === "professional" ? (
                        <DataListCardRow key={`pro-${row.data.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
                              {displayNameOf(row.data)}
                            </p>
                            <div className="shrink-0">
                              <TableActionsMenu actions={rowActions(row.data)} />
                            </div>
                          </div>
                          <div className="mt-3 space-y-3">
                            <DataListCardField label={t("professionalsSection.columnEmail")}>
                              <span className="text-muted-foreground">{row.data.email}</span>
                            </DataListCardField>
                            <DataListCardField
                              label={t("professionalsSection.columnProfession")}
                            >
                              <span className="text-muted-foreground">
                                {row.data.professionDisplayName ??
                                  professionLabel(row.data.professionSlug)}
                              </span>
                            </DataListCardField>
                            <DataListCardField label={t("professionalsSection.columnStatus")}>
                              {statusCell(row.data)}
                            </DataListCardField>
                            <DataListCardField label={t("professionalsSection.columnCreated")}>
                              <span className="text-muted-foreground">
                                {formatDisplayDate(row.data.createdAt)}
                              </span>
                            </DataListCardField>
                          </div>
                        </DataListCardRow>
                      ) : (
                        <DataListCardRow key={`invite-${row.data.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
                              {row.data.email}
                            </p>
                            {isRoot ? (
                              <div className="shrink-0">
                                {resendingId === row.data.id ? (
                                  <div className="flex h-9 w-9 items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                  </div>
                                ) : (
                                  <TableActionsMenu
                                    disabled={resendingId !== null || cancellingId !== null}
                                    actions={inviteActions(row.data)}
                                  />
                                )}
                              </div>
                            ) : null}
                          </div>
                          <div className="mt-3 space-y-3">
                            <DataListCardField
                              label={t("professionalsSection.columnProfession")}
                            >
                              <span className="text-muted-foreground">
                                {professionLabel(row.data.professionSlug)}
                              </span>
                            </DataListCardField>
                            <DataListCardField label={t("professionalsSection.columnStatus")}>
                              <StatusBadge variant={row.data.expired ? "danger" : "warning"}>
                                {row.data.expired
                                  ? t("professionalsSection.statusExpired")
                                  : t("professionalsSection.statusPendingInvite")}
                              </StatusBadge>
                            </DataListCardField>
                            <DataListCardField label={t("professionalsSection.columnInvitedBy")}>
                              <span className="text-muted-foreground">
                                {row.data.invitedByEmail ?? "—"}
                              </span>
                            </DataListCardField>
                          </div>
                        </DataListCardRow>
                      ),
                    )}
                  </div>
                }
              />
            )}

            {totalRows > 0 ? (
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("professionalsSection.rowsPerPage")}
                pageOfLabel={t("professionalsSection.pageOf", {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
            ) : null}
          </>
        )}
      </TableCard>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("professionalsSection.inviteDialogTitle")}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const email = inviteEmail.trim();
              if (!email || !inviteProfession) return;
              inviteMutation.mutate({ professionSlug: inviteProfession, email });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="invite-profession">
                {t("professionalsSection.inviteProfessionLabel")}
              </Label>
              <Select value={inviteProfession} onValueChange={setInviteProfession}>
                <SelectTrigger id="invite-profession">
                  <SelectValue
                    placeholder={t("professionalsSection.inviteProfessionPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {professions.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      {p.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">{t("professionalsSection.inviteEmailLabel")}</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="profesional@example.com"
                disabled={inviteMutation.isPending}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                {t("professionalsSection.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  !inviteEmail.trim() || !inviteProfession || inviteMutation.isPending
                }
              >
                {inviteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("professionalsSection.sending")}
                  </>
                ) : (
                  t("professionalsSection.sendInvite")
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={cancelDialogInvite != null}
        onOpenChange={(open) => {
          if (!open) setCancelDialogInvite(null);
        }}
        title={t("professionalsSection.cancelInviteDialogTitle")}
        description={t("professionalsSection.cancelInviteDialogDescription")}
        confirmText={t("professionalsSection.cancelInviteConfirm")}
        cancelText={t("professionalsSection.cancel")}
        variant="destructive"
        onConfirm={() => {
          if (cancelDialogInvite) {
            setCancellingId(cancelDialogInvite.id);
            cancelMutation.mutate(cancelDialogInvite);
          }
        }}
      />

      <ConfirmationDialog
        open={toDelete != null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title={t("professionalsSection.deleteConfirmTitle")}
        description={t("professionalsSection.deleteConfirmDescription")}
        confirmText={t("professionalsSection.delete")}
        cancelText={t("professionalsSection.cancel")}
        variant="destructive"
        isLoading={deletingId !== null}
        onConfirm={() => {
          if (toDelete) {
            setDeletingId(toDelete.id);
            deleteMutation.mutate(toDelete.id);
          }
        }}
      />
    </div>
  );
}
